import {
  useAddPageModule,
  useFetchPageModules,
  useFetchStore,
  useUpdatePageModule,
} from 'api/store/hooks';
import { moduleNames, modules as storeModules } from 'pages/Stores/components/modules';
import { useStoreArtistAvailableItems } from 'pages/ModulePage/moduleTypes/artists/useArtistAvailableItems';
import { useStoreBannerAvailableItems } from 'pages/ModulePage/moduleTypes/banner/useBannerAvailableItems';
import { useStoreProductAvailableItems } from 'pages/ModulePage/moduleTypes/favoriteProducts/useProductAvailableItems';
import { useStoreLookAvailableItems } from 'pages/ModulePage/moduleTypes/looks/useLookAvailableItems';
import { useStoreVideoAvailableItems } from 'pages/ModulePage/moduleTypes/video/useVideoAvailableItems';
import { useStoreEventAvailableItems } from 'pages/ModulePage/moduleTypes/events/useEventAvailableItems';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { STORE } from 'constants/routes';
import { toastr } from 'utils';
import PageModulePage from './ModulePage';
import { pageModuleTypeKeys, pageModuleTypes } from './pageModuleTypes';

const storeAvailableItemsHooks = {
  [moduleNames.MULTIPLE_LOOK]: useStoreLookAvailableItems,
  [moduleNames.FAVORITE_PRODUCTS]: useStoreProductAvailableItems,
  [moduleNames.BANNER]: useStoreBannerAvailableItems,
  [moduleNames.VIDEO]: useStoreVideoAvailableItems,
  [moduleNames.ARTISTS]: useStoreArtistAvailableItems,
  [moduleNames.EVENTS]: useStoreEventAvailableItems,
};

const StoreModulePage = () => {
  const { moduleUid } = useParams();
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { t: tApp } = useTranslation('app');
  const navigate = useNavigate();
  const { data: store, isLoading: isLoadingStore } = useFetchStore();
  const {
    data: pageModules = [],
    isLoading: isLoadingPageModules,
    isError: isPageModulesError,
  } = useFetchPageModules();
  const isLoading = isLoadingStore || isLoadingPageModules;
  const { mutate: addPageModule, isPending } = useAddPageModule();
  const { mutate: updatePageModule, isPending: isUpdatePending } = useUpdatePageModule();
  const isEditMode = !!moduleUid;

  const moduleTypes = useMemo(() => {
    const enabledModuleNames = storeModules
      .filter((module) => isEditMode || !module.disabled)
      .map((m) => m.name);

    return Object.fromEntries(
      enabledModuleNames
        .filter((name) => pageModuleTypes[name])
        .map((name) => [name, pageModuleTypes[name](storeAvailableItemsHooks[name])]),
    );
  }, [isEditMode]);

  const existingModule = useMemo(
    () => pageModules.find((module) => module.uid === moduleUid),
    [moduleUid, pageModules],
  );
  const initialModuleTypeKey = existingModule
    ? pageModuleTypeKeys[existingModule.module_type]
    : null;

  useEffect(() => {
    if (isEditMode && !isLoadingPageModules && !isPageModulesError && !existingModule) {
      navigate(STORE, { replace: true });
    }
  }, [existingModule, isEditMode, isLoadingPageModules, isPageModulesError, navigate]);

  const handleSave = async (moduleTypeKey, values, { skipContentUpdate = false } = {}) => {
    const moduleMeta = moduleTypes[moduleTypeKey];
    if (!moduleMeta || !store) return;

    try {
      const content = skipContentUpdate
        ? undefined
        : await moduleMeta.buildPayload(values, { isEditMode });

      if (isEditMode) {
        updatePageModule(
          {
            moduleUid,
            name: values.pageModuleName,
            moduleType: existingModule?.module_type || moduleMeta.moduleName.toLowerCase(),
            content,
          },
          {
            onSuccess: () => navigate(STORE),
          },
        );
        return;
      }

      addPageModule(
        {
          name: values.pageModuleName,
          module_type: moduleMeta.moduleName.toLowerCase(),
          content,
          order: pageModules.length,
        },
        {
          onSuccess: (data) => navigate(STORE, { state: { scrollToItemId: data.uid } }),
        },
      );
    } catch {
      toastr.error(t('failedToCreatePageModule'));
    }
  };

  if (isEditMode && !isLoadingPageModules && !isPageModulesError && !existingModule) {
    return null;
  }

  const currentBreadcrumbLabel = isEditMode ? (
    isLoading ? (
      <Skeleton width={120} />
    ) : (
      existingModule?.name || t('editModule')
    )
  ) : (
    t('createModule')
  );

  return (
    <PageModulePage
      moduleTypes={moduleTypes}
      onSave={handleSave}
      initialModuleTypeKey={initialModuleTypeKey}
      existingModule={existingModule}
      isPending={isPending || isUpdatePending}
      title={t(isEditMode ? 'editModule' : 'createModule')}
      description={t('moduleDetailsSubtitle')}
      saveLabel={t(isEditMode ? 'editModule' : 'createModule')}
      breadcrumbs={[
        { key: 'store', label: tApp('breadcrumbs.store'), to: STORE },
        { key: 'page-modules', label: tApp('breadcrumbs.pageModules'), to: STORE },
        { label: currentBreadcrumbLabel },
      ]}
      loading={isLoading}
      isEditMode={isEditMode}
    />
  );
};

export default StoreModulePage;
